<template>
  <div
    class="my-4 flex shrink-0 grow-0 basis-auto items-center justify-end gap-2">
    <h1 class="mr-auto">
      {{ capitalize(i18n.t('backgroundjob', { count: 2 })) }}
    </h1>

    <!-- Status Filter -->
    <SmDropdown :close-on-click-inside="false" max-height="30vh">
      <template #trigger>
        <SmButton outline size="small">
          <div style="display: flex; align-items: center">
            <span class="mr-3">{{ i18n.t('filter') }}</span>
            <span
              v-if="currentStatusFilter?.length"
              class="mr-2 rounded-full bg-gray-200 px-2 py-1 text-xs font-semibold text-black dark:bg-gray-700 dark:text-white">
              {{ currentStatusFilter?.length }}
            </span>
            <ChevronDown :size="20" />
          </div>
        </SmButton>
      </template>

      <div style="padding: 1rem">
        <template
          v-if="
            !isEqual(sortBy(currentStatusFilter), sortBy(defaultStatusFilter))
          ">
          <SmDropdownItem
            value="reset"
            class="flex w-48 justify-between"
            @click="resetFilter()">
            {{ i18n.t('resetFilters') }}
            <Undo2 class="ml-auto text-contrast" :size="18" />
          </SmDropdownItem>

          <hr class="mb-4 mt-2" />
        </template>

        <SmDropdownItem
          v-for="_status in availableStatus"
          :key="_status.toString()"
          :value="_status"
          class="flex w-48 justify-between"
          @click="handleStatusFilterSelect(_status)">
          <SmTooltip :content="getStatus(_status).text" :active="false">
            <div class="flex items-center gap-2">
              <span
                class="indicator-dot shrink-0"
                :class="getStatus(_status).color"></span>
              <TextMultiline :text="getStatus(_status).text" />
            </div>
          </SmTooltip>

          <Check
            v-if="currentStatusFilter?.some((filter) => filter === _status)"
            :size="18"
            class="ml-auto" />
        </SmDropdownItem>
      </div>
    </SmDropdown>
  </div>
</template>

<script setup lang="ts">
  import { Check } from 'lucide-vue-next'
  import { BackgroundJobStatus } from '@/client'
  import dayjs from 'dayjs'
  import { useI18n } from 'vue-i18n'
  import { isEqual, sortBy } from 'lodash-es'

  import utc from 'dayjs/plugin/utc'
  import timezone from 'dayjs/plugin/timezone' // dependent on utc plugin
  import localizedFormat from 'dayjs/plugin/localizedFormat'
  import { backgroundjobStore } from '@/stores/backgroundjob/backgroundjob.store'
  import { removeFilter, updateFilter } from '@/utils/filter'
  import { BackgroundjobFilterInstance } from '@/stores/backgroundjob/backgroundjob.utils'
  import { capitalize } from '@/utils/formatter/formatter'
  import { getStatus } from '@/stores/backgroundjob/backgroundjob.utils'
  import { ChevronDown, Undo2 } from 'lucide-vue-next'

  dayjs.extend(utc)
  dayjs.extend(timezone)
  dayjs.extend(localizedFormat)

  const emit = defineEmits(['updateFilter'])

  const i18n = useI18n()

  /**
   * Filters
   */
  const filters = ref<BackgroundjobFilterInstance[]>([])

  // Statusfilter
  export interface Status {
    color: string
    text: string
  }

  const availableStatus: Array<BackgroundJobStatus> = [
    BackgroundJobStatus.ENQUEUED,
    BackgroundJobStatus.PAUSED,
    BackgroundJobStatus.COMPLETED,
    BackgroundJobStatus.USER_ACTION_REQUIRED,
    BackgroundJobStatus.AWAITS_SOFTWARE_AUTHORIZATION,
    BackgroundJobStatus.DEPRECATED,
    BackgroundJobStatus.ABORTED,
    BackgroundJobStatus.ERROR,
  ]

  const defaultStatusFilter = ref<BackgroundJobStatus[]>([
    BackgroundJobStatus.ENQUEUED,
    BackgroundJobStatus.PAUSED,
    BackgroundJobStatus.COMPLETED,
    BackgroundJobStatus.USER_ACTION_REQUIRED,
    BackgroundJobStatus.AWAITS_SOFTWARE_AUTHORIZATION,
    BackgroundJobStatus.DEPRECATED,
    BackgroundJobStatus.ABORTED,
    BackgroundJobStatus.ERROR,
  ])

  const currentStatusFilter = ref<BackgroundJobStatus[]>(
    defaultStatusFilter.value
  )

  function resetFilter() {
    currentStatusFilter.value = defaultStatusFilter.value
    setStatusFilter()
  }

  function handleStatusFilterSelect(status: BackgroundJobStatus) {
    if (currentStatusFilter.value.some((s) => s === status)) {
      //remove filter
      currentStatusFilter.value = currentStatusFilter.value.filter(
        (s) => s !== status
      )
    } else {
      // add filter
      currentStatusFilter.value = [...currentStatusFilter.value, status]
    }

    setStatusFilter()
  }

  function setStatusFilter() {
    const newFilter = new backgroundjobStore.filter.StatusFilter(
      currentStatusFilter.value
    )

    if (!newFilter) {
      removeFilter(filters, 'StatusFilter')
      return
    }

    updateFilter(filters, newFilter)
    emit('updateFilter', filters.value)
  }

  onMounted(async () => {
    setStatusFilter()
  })
</script>

<style lang="scss" scoped>
  .indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
</style>
